/**
 * 引用源追溯页面 — CitationSources（F4，对齐概念稿 citations.html）。
 *
 * GET  /api/citation-sources?brand_id&platform&page&size  引用源聚合列表（主域归一 + 分平台透视）
 * GET  /api/citation-sources/{domain}/articles            文章级下钻（子域仅在此层出现）
 * POST /api/citation-sources/mark-owned                   标记/取消自有（乐观更新，失败回滚）
 *
 * 结构：分平台 Tabs + info 胶囊（switchover_date）→ g83（左引用源表 / 右文章级下钻面板）
 * → 底部历史汇总卡（legacy_bucket，未分平台数据诚实呈现，PRD Q6）。
 * 预警联动：wow_delta ≤ -0.3 的行显示下降徽章。
 * 后端端点并行开发中：全部按契约盲写 + EmptyState v2 降级，不红屏。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import {
  getCitationSources,
  getCitationSourceArticles,
  markCitationSourceOwned,
  assessMediaMatch,
} from "../api/citationSources";
import { useCompanyStore } from "../store/companyStore";
import { TIER_MAP, platformLabel } from "../utils/constants";
import { formatDateTime } from "../utils/format";
import type {
  CitationArticle,
  CitationSource,
  CitationSourcesData,
} from "../types";

/** 分平台 Tabs（值为后端 platform 枚举中文名；空串 = 全部）。 */
const PLATFORM_TABS: { value: string; label: string }[] = [
  { value: "", label: "全部" },
  { value: "豆包", label: "豆包" },
  { value: "DeepSeek", label: "DeepSeek" },
  { value: "通义千问", label: "通义千问" },
  { value: "元宝", label: "元宝" },
  { value: "文心一言", label: "文心一言" },
];

/** 权重档位徽章（未命中回退灰）。 */
function TierBadge({ tier }: { tier?: string }) {
  const info = (tier && TIER_MAP[tier]) || {
    label: tier || "—",
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

/** 环比展示：≤ -0.3 显示下降徽章（预警联动），其余着色文本。 */
function WowDelta({ value }: { value?: number }) {
  if (value === undefined || value === null) {
    return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>;
  }
  const pct = `${value > 0 ? "+" : ""}${(value * 100).toFixed(0)}%`;
  if (value <= -0.3) {
    return <Badge label={`↓ ${Math.abs(value * 100).toFixed(0)}%`} variant="error" />;
  }
  return (
    <Typography
      sx={{
        fontSize: 12,
        color: value < 0 ? "error.main" : value > 0 ? "success.main" : "text.secondary",
      }}
    >
      {pct}
    </Typography>
  );
}

export default function CitationSources() {
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);

  const [platform, setPlatform] = useState("");
  const [page, setPage] = useState(0); // MUI 0 基；请求时 +1
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [data, setData] = useState<CitationSourcesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [actionError, setActionError] = useState("");

  // 媒体匹配评估：标记哪些溯源域名无对应可投媒体（仅写状态，不入投放库）
  const [assessing, setAssessing] = useState(false);
  const [assessMsg, setAssessMsg] = useState("");

  // 下钻面板
  const [selectedDomain, setSelectedDomain] = useState("");
  const [articles, setArticles] = useState<CitationArticle[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesFailed, setArticlesFailed] = useState(false);

  const fetchList = useCallback(async () => {
    if (!selectedBrandId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      const result = await getCitationSources({
        brand_id: selectedBrandId,
        platform: platform || undefined,
        page: page + 1,
        size: rowsPerPage,
      });
      setData(result);
    } catch {
      setData(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, platform, page, rowsPerPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // 切换平台/品牌时重置下钻选择
  useEffect(() => {
    setSelectedDomain("");
    setArticles([]);
  }, [selectedBrandId, platform]);

  /** 文章级下钻。 */
  useEffect(() => {
    if (!selectedDomain || !selectedBrandId) return;
    setArticlesLoading(true);
    setArticlesFailed(false);
    getCitationSourceArticles(selectedDomain, {
      brand_id: selectedBrandId,
      platform: platform || undefined,
      size: 50,
    })
      .then((res) => setArticles(res.items || []))
      .catch(() => {
        setArticles([]);
        setArticlesFailed(true);
      })
      .finally(() => setArticlesLoading(false));
  }, [selectedDomain, selectedBrandId, platform]);

  /** 评估媒体匹配（仅只读评估 + 落状态，不生成投放库；失败不红屏）。 */
  const handleAssessMatch = async () => {
    if (!selectedBrandId || assessing) return;
    setAssessing(true);
    setActionError("");
    setAssessMsg("");
    try {
      const r = await assessMediaMatch(selectedBrandId);
      setAssessMsg(
        `已评估 ${r.domains_total} 个溯源域名，其中 ${r.no_match_domains.length} 个无媒体匹配`,
      );
      fetchList();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "评估失败");
    } finally {
      setAssessing(false);
    }
  };

  /** 标记/取消自有：乐观更新，失败回滚。 */
  const handleToggleOwned = async (row: CitationSource) => {
    if (!selectedBrandId) return;
    const nextOwned = !row.owned;
    setActionError("");
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) =>
              it.domain === row.domain ? { ...it, owned: nextOwned } : it,
            ),
          }
        : prev,
    );
    try {
      await markCitationSourceOwned({
        domain: row.domain,
        brand_id: selectedBrandId,
        owned: nextOwned,
      });
    } catch (err) {
      // 回滚
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it) =>
                it.domain === row.domain ? { ...it, owned: row.owned } : it,
              ),
            }
          : prev,
      );
      setActionError(
        err instanceof Error ? err.message : "标记失败，请稍后重试",
      );
    }
  };

  const columns: DataTableColumn<CitationSource>[] = [
    {
      key: "domain",
      label: "归一化主域",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{row.domain}</Typography>
          {row.owned && <Badge label="自有" color="primary.main" bgColor="brand.50" />}
          {row.domain === selectedDomain && (
            <Badge label="下钻中" color="primary.main" bgColor="brand.50" />
          )}
        </Box>
      ),
    },
    {
      key: "tier",
      label: "权重档位",
      align: "center",
      render: (row) => <TierBadge tier={row.tier} />,
    },
    {
      key: "ecosystem",
      label: "生态归属",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>{row.ecosystem || "—"}</Typography>
      ),
    },
    {
      key: "citation_count",
      label: "被引次数",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.citation_count}</Typography>
      ),
    },
    {
      key: "platform_count",
      label: "平台数",
      align: "right",
      render: (row) => <Typography sx={{ fontSize: 13 }}>{row.platform_count}</Typography>,
    },
    {
      key: "match",
      label: "媒体匹配",
      align: "center",
      render: (row) => {
        if (row.match_status === "matched") {
          return <Badge label={`可投 ${row.matched_count ?? 0} 站`} variant="success" />;
        }
        if (row.match_status === "no_match") {
          return <Badge label="无媒体匹配" variant="warning" />;
        }
        return <Badge label="未评估" variant="default" />;
      },
    },
    {
      key: "wow_delta",
      label: "环比",
      align: "right",
      render: (row) => <WowDelta value={row.wow_delta} />,
    },
    {
      key: "ops",
      label: "操作",
      align: "center",
      render: (row) => (
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleOwned(row);
          }}
        >
          {row.owned ? "取消标记" : "标记自有"}
        </Button>
      ),
    },
  ];

  const articleColumns: DataTableColumn<CitationArticle>[] = [
    {
      key: "title",
      label: "子域 / 标题",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {row.subdomain || "—"}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {row.title || row.url}
          </Typography>
        </Box>
      ),
    },
    {
      key: "platforms",
      label: "被引平台",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.platforms?.length
            ? row.platforms.map(platformLabel).join(" · ")
            : "—"}
        </Typography>
      ),
    },
    {
      key: "last_cited_at",
      label: "最近引用",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(row.last_cited_at)}
        </Typography>
      ),
    },
  ];

  if (!selectedBrandId) {
    return (
      <Box>
        <PageHeader
          title="引用源追溯"
          breadcrumbs={[{ label: "GEO 分析" }, { label: "引用源追溯" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先在顶栏选择品牌"
              subtitle="引用源按品牌维度聚合，选择品牌后自动加载"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  const legacy = data?.legacy_bucket;

  return (
    <Box>
      <PageHeader
        title="引用源追溯"
        breadcrumbs={[{ label: "GEO 分析" }, { label: "引用源追溯" }]}
        subtitle="AI 引用的是谁的内容、是不是我的 · 来源 → 文章两级下钻"
      />

      {/* 分平台透视 Tabs + switchover info 胶囊 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tabs
              value={platform}
              onChange={(_, v) => {
                setPlatform(v);
                setPage(0);
              }}
              sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
            >
              {PLATFORM_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="outlined"
              disabled={assessing}
              onClick={handleAssessMatch}
              sx={{ mr: 1 }}
            >
              {assessing ? "评估中..." : "评估媒体匹配"}
            </Button>
            {data?.switchover_date && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: "semanticBg.info",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "info.main",
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "info.main", whiteSpace: "nowrap" }}>
                  分平台数据自 {data.switchover_date} 起生效，此前引用归入「历史汇总」
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}

      {assessMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAssessMsg("")}>
          {assessMsg}
        </Alert>
      )}

      {/* g83：左引用源表 / 右文章级下钻面板 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>引用源列表</Typography>
            {data && (
              <Badge label={`归一化主域 ×${data.total ?? data.items.length}`} />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              按被引次数排序 · 点击行下钻文章明细
            </Typography>
          </Box>
          {loading ? (
            <Loading fullPage />
          ) : failed ? (
            <Card>
              <CardContent>
                <EmptyState
                  text="引用源数据暂未接入"
                  source="由引用解析批次产出 · 端点未就绪时显示空态"
                  hint="平台切换后重试，或稍后再来"
                />
              </CardContent>
            </Card>
          ) : (
            <DataTable
              columns={columns}
              data={data?.items || []}
              total={data?.total || 0}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={(size) => {
                setRowsPerPage(size);
                setPage(0);
              }}
              onRowClick={(row) => setSelectedDomain(row.domain)}
              getRowId={(row) => row.domain}
              emptyText="暂无引用源数据"
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {selectedDomain ? `下钻 · ${selectedDomain} 文章级明细` : "文章级明细"}
                </Typography>
                {selectedDomain && !articlesLoading && !articlesFailed && (
                  <Badge label={`${articles.length} 篇`} />
                )}
              </Box>
              {!selectedDomain ? (
                <EmptyState
                  text="点击左侧引用源行"
                  subtitle="查看该主域下的文章级引用明细"
                  source="子域仅在文章级明细出现（RR1）"
                />
              ) : articlesLoading ? (
                <Loading text="正在加载文章明细..." />
              ) : articlesFailed ? (
                <EmptyState
                  text="文章明细暂未接入"
                  source="由引用解析批次产出 · 端点未就绪时显示空态"
                />
              ) : articles.length === 0 ? (
                <EmptyState
                  text="该主域暂无文章级明细"
                  source="由引用解析批次产出"
                />
              ) : (
                <DataTable
                  columns={articleColumns}
                  data={articles}
                  showPagination={false}
                  getRowId={(row) => row.url}
                />
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  lineHeight: 1.7,
                  mt: 1.5,
                }}
              >
                文章级保留原始子域与 URL（RR1）；「标记自有」后该 URL
                自动进入收录台账 T+1/T+3/T+7 复检池（RR2）。
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 历史汇总（unknown 平台，诚实呈现） */}
      {legacy && (
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                历史汇总 · 未分平台引用
              </Typography>
              <Badge label="unknown" />
            </Box>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  历史引用次数
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 0.5 }}>
                  {legacy.citation_count}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  涉及文章
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 0.5 }}>
                  {legacy.article_count}
                  <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>
                    {" "}篇
                  </Typography>
                </Typography>
              </Box>
              {legacy.note && (
                <Typography
                  sx={{ flex: 1, minWidth: 240, fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}
                >
                  {legacy.note}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
